import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Check, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';

interface Props {
  status: AutoSaveStatus;
  /** Relance la dernière écriture échouée. */
  retry?: (() => void) | null;
  className?: string;
}

/**
 * L'état de la sauvegarde automatique, là où était le bouton « Enregistrer ».
 *
 * C'est la contrepartie de l'auto-save : sans lui, on échange la friction
 * contre l'échec silencieux. `idle` n'affiche rien — annoncer « rien à
 * enregistrer » sur un écran qu'on vient d'ouvrir serait du bruit.
 */
const AutoSaveIndicator: React.FC<Props> = ({ status, retry, className }) => {
  const { t } = useTranslation();

  if (status === 'idle') return null;

  if (status === 'error') {
    return (
      <span className={cn('flex items-center gap-2 text-xs text-destructive', className)}>
        <AlertCircle size={13} />
        {t('autoSave.error')}
        {retry && (
          <button
            type="button"
            onClick={retry}
            className="underline underline-offset-2 hover:no-underline"
          >
            {t('autoSave.retry')}
          </button>
        )}
      </span>
    );
  }

  return (
    <span className={cn('flex items-center gap-1.5 text-xs text-muted-foreground', className)}>
      {status === 'saving'
        ? <Loader2 size={13} className="animate-spin" />
        : <Check size={13} className="text-success" />}
      {t(status === 'saving' ? 'autoSave.saving' : 'autoSave.saved')}
    </span>
  );
};

export default AutoSaveIndicator;
